class RufftailSmartSearch {
  constructor(container) {
    this.container = container;
    this.form = container.querySelector('[data-rf-search-form]');
    this.input = container.querySelector('[data-rf-search-input]');
    this.panel = container.querySelector('[data-rf-search-panel]');
    this.clearButton = container.querySelector('[data-rf-clear]');
    this.recentGroup = container.querySelector('[data-rf-recent-group]');
    this.recentList = container.querySelector('[data-rf-recent-list]');
    this.discoveryBlocks = container.querySelectorAll('[data-rf-discovery]');
    this.resultsWrap = container.querySelector('[data-rf-results-wrap]');
    this.resultsNode = container.querySelector('[data-rf-results]');
    this.collectionsNode = container.querySelector('[data-rf-collections]');
    this.emptyState = container.querySelector('[data-rf-empty-state]');
    this.noResults = container.querySelector('[data-rf-no-results]');
    this.viewAll = container.querySelector('[data-rf-view-all]');
    this.searchUrl = container.dataset.searchUrl || '/search';
    this.predictiveUrl = container.dataset.predictiveUrl || '/search/suggest';
    this.placeholderItems = (container.dataset.placeholderItems || '')
      .split('||')
      .map((item) => item.trim())
      .filter(Boolean);
    this.searchMode = container.dataset.searchMode || 'default';
    this.recentKey = 'rf_recent_searches';
    this.debounceTimer = null;
    this.placeholderTimer = null;
    this.activeFetchId = 0;
    this.currentIndex = 0;

    this.bindEvents();
    this.renderRecentSearches();
    this.startPlaceholderRotation();
  }

  bindEvents() {
    this.input?.addEventListener('focus', () => this.openPanel());
    this.input?.addEventListener('input', () => this.handleInput());
    this.input?.addEventListener('keydown', (event) => this.handleKeydown(event));
    this.form?.addEventListener('submit', () => this.storeRecentSearch(this.input.value));
    this.clearButton?.addEventListener('click', () => this.clearInput());

    this.container.querySelectorAll('[data-rf-fill]').forEach((button) => {
      button.addEventListener('click', () => {
        this.input.value = button.dataset.rfFill || '';
        this.handleInput(true);
        this.input.focus();
      });
    });

    document.addEventListener('click', (event) => {
      if (!this.container.contains(event.target)) {
        this.closePanel();
      }
    });
  }

  openPanel() {
    this.container.classList.add('is-open');
    this.panel.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  closePanel() {
    this.container.classList.remove('is-open');
    this.panel.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
  }

  clearInput() {
    this.input.value = '';
    this.input.focus();
    this.clearButton.hidden = true;
    this.resetDiscoveryState();
  }

  handleInput(runImmediately = false) {
    const query = this.input.value.trim();
    this.clearButton.hidden = query.length === 0;
    this.openPanel();

    if (!query) {
      this.resetDiscoveryState();
      return;
    }

    clearTimeout(this.debounceTimer);
    const runSearch = () => this.fetchResults(query);

    if (runImmediately) {
      runSearch();
      return;
    }

    this.debounceTimer = window.setTimeout(runSearch, 180);
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.closePanel();
      this.input.blur();
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
      return;
    }

    const focusables = Array.from(
      this.panel.querySelectorAll('a[href], button:not([hidden]):not([disabled])')
    ).filter((element) => !element.closest('[hidden]'));

    if (!focusables.length) {
      return;
    }

    event.preventDefault();
    const current = focusables.indexOf(document.activeElement);
    const nextIndex =
      event.key === 'ArrowDown'
        ? (current + 1 + focusables.length) % focusables.length
        : (current - 1 + focusables.length) % focusables.length;

    focusables[nextIndex].focus();
  }

  startPlaceholderRotation() {
    if (!this.input || this.placeholderItems.length < 2) {
      return;
    }

    this.placeholderTimer = window.setInterval(() => {
      if (document.activeElement === this.input || this.input.value.trim()) {
        return;
      }

      this.currentIndex = (this.currentIndex + 1) % this.placeholderItems.length;
      this.input.setAttribute('placeholder', this.placeholderItems[this.currentIndex]);
    }, 2400);
  }

  renderRecentSearches() {
    const items = this.getRecentSearches();
    this.recentList.innerHTML = '';

    if (!items.length) {
      this.recentGroup.hidden = true;
      return;
    }

    this.recentGroup.hidden = false;

    items.forEach((item) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'rf-smart-search__chip';
      button.textContent = item;
      button.addEventListener('click', () => {
        this.input.value = item;
        this.handleInput(true);
        this.input.focus();
      });
      this.recentList.appendChild(button);
    });
  }

  getRecentSearches() {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(this.recentKey) || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  storeRecentSearch(value) {
    const term = (value || '').trim();
    if (!term) {
      return;
    }

    const nextItems = [term, ...this.getRecentSearches().filter((item) => item !== term)].slice(0, 6);
    window.localStorage.setItem(this.recentKey, JSON.stringify(nextItems));
    this.renderRecentSearches();
  }

  resetDiscoveryState() {
    this.resultsWrap.hidden = true;
    this.noResults.hidden = true;
    this.emptyState.hidden = false;
    this.discoveryBlocks.forEach((block) => {
      block.hidden = this.searchMode === 'mobile' && block !== this.discoveryBlocks[0];
    });
    this.resultsNode.innerHTML = '';
    this.collectionsNode.innerHTML = '';
    this.updateViewAll('');
  }

  async fetchResults(query) {
    const fetchId = ++this.activeFetchId;
    const url = `${this.predictiveUrl}.json?q=${encodeURIComponent(query)}&resources[type]=product,collection&resources[limit]=4&resources[limit_scope]=each&resources[options][unavailable_products]=hide`;

    try {
      const response = await fetch(url, {
        headers: {
          Accept: 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Predictive search failed with status ${response.status}`);
      }

      const payload = await response.json();
      if (fetchId !== this.activeFetchId) {
        return;
      }

      const products = payload?.resources?.results?.products || [];
      const collections = payload?.resources?.results?.collections || [];
      this.renderPredictiveResults(query, products, collections);
    } catch (error) {
      if (fetchId !== this.activeFetchId) {
        return;
      }
      this.renderPredictiveResults(query, [], []);
    }
  }

  renderPredictiveResults(query, products, collections) {
    this.discoveryBlocks.forEach((block) => {
      block.hidden = true;
    });
    this.emptyState.hidden = true;
    this.resultsNode.innerHTML = '';
    this.collectionsNode.innerHTML = '';

    if (!products.length && !collections.length) {
      this.resultsWrap.hidden = true;
      this.noResults.hidden = false;
      this.updateViewAll(query);
      return;
    }

    this.resultsWrap.hidden = false;
    this.noResults.hidden = true;
    this.updateViewAll(query);

    products.forEach((product) => {
      const anchor = document.createElement('a');
      anchor.className = 'rf-smart-search__result';
      anchor.href = product.url;
      anchor.innerHTML = `
        <span class="rf-smart-search__thumb">
          ${product.featured_image?.url ? `<img src="${product.featured_image.url}" alt="${this.escapeHtml(product.title)}" loading="lazy">` : ''}
        </span>
        <span class="rf-smart-search__meta">
          <span class="rf-smart-search__title">${this.escapeHtml(product.title)}</span>
          <span class="rf-smart-search__subtext">${this.escapeHtml(product.vendor || 'Popular pick')}</span>
        </span>
        <span>
          <span class="rf-smart-search__price">${product.price ? product.price : ''}</span>
          ${product.compare_at_price ? `<span class="rf-smart-search__compare">${product.compare_at_price}</span>` : ''}
        </span>
      `;
      anchor.addEventListener('click', () => this.storeRecentSearch(query));
      this.resultsNode.appendChild(anchor);
    });

    collections.forEach((collection) => {
      const anchor = document.createElement('a');
      anchor.className = 'rf-smart-search__collection';
      anchor.href = collection.url;
      anchor.innerHTML = `
        <span>${this.escapeHtml(collection.title)}</span>
        <span class="rf-smart-search__arrow">+</span>
      `;
      this.collectionsNode.appendChild(anchor);
    });
  }

  updateViewAll(query) {
    if (!this.viewAll) {
      return;
    }

    const trimmed = (query || '').trim();
    if (!trimmed) {
      this.viewAll.hidden = true;
      this.viewAll.setAttribute('href', this.searchUrl);
      return;
    }

    this.viewAll.hidden = false;
    this.viewAll.setAttribute('href', `${this.searchUrl}?q=${encodeURIComponent(trimmed)}&options%5Bprefix%5D=last`);
  }

  escapeHtml(value) {
    return String(value || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-rf-smart-search]').forEach((element) => {
    if (!element.dataset.rfSmartSearchMounted) {
      element.dataset.rfSmartSearchMounted = 'true';
      new RufftailSmartSearch(element);
    }
  });
});
